// Project actual saved source settings onto the DAG; never infer filters from names/goals.
const parseObject = (value) => {
  if (typeof value === 'string') {
    try { value = JSON.parse(value); } catch (_) { return {}; }
  }
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
};

export const connectorPreparationSteps = (connector, t) => {
  const saved = parseObject(connector?.datasource?.dataPreview ?? connector?.datasource?.data);
  const pipeline = parseObject(saved._connector_pipeline);
  const names = parseObject(saved._connector_pipeline_step_names);
  const steps = [];
  // This is an explicit native connector parameter, not semantic keyword routing.
  if (String(connector?.type || '').toLowerCase() === 'outlookapi'
      && [true, 1, 'true', '1'].includes(saved.exclude_junk)) {
    steps.push({ id: 'native-exclude-junk', native: true, operation: 'native',
      settings: { exclude_junk: true },
      name: t('connector_setup.outlook.exclude_junk'),
      help: t('connector_setup.outlook.exclude_junk_help') });
  }
  if (String(connector?.type || '').toLowerCase() === 'outlookapi') {
    [['emails', 'senders'], ['searchterms', 'search'], ['folders', 'folders']].forEach(([key, label]) => {
      const value = saved[key];
      const present = Array.isArray(value) ? value.length > 0 : typeof value === 'string' && Boolean(value.trim());
      if (present) steps.push({ id: `native-${key}`, native: true, operation: 'native',
        settings: { [key]: value },
        name: t(`connector_setup.pipeline.native_filters.${label}`),
        help: t(`connector_setup.pipeline.native_filters.${label}_help`) });
    });
  }
  if (Array.isArray(pipeline.steps)) {
    pipeline.steps.slice(0, 16).forEach((step, index) => {
      if (!step || typeof step !== 'object') return;
      let operation = step.operation;
      let settings = parseObject(step.settings_json);
      if (['action', 'plugin'].includes(operation) && settings.action_type === 'preparerecord') {
        operation = settings.settings?.operation || operation;
        settings = parseObject(settings.settings?.settings_json);
      }
      steps.push({ id: `step-${step.id || index}`, stepId: step.id,
        contract: step,
        settings, conditions: step.when_json,
        name: typeof names[step.id] === 'string' && names[step.id].trim() ? names[step.id].slice(0, 200)
          : typeof step.name === 'string' && step.name.trim() ? step.name.slice(0, 200) : t('workflow.preparerecord.title'),
        operation, mode: pipeline.mode,
        help: t(`connector_setup.pipeline.operations.${operation}`, {
          defaultValue: t('connector_setup.pipeline.operations.action'),
        }) });
    });
  }
  return steps.map((step, index) => ({ ...step,
    inputLabel: steps[index - 1]?.name || connector.datasource?.name || connector.type,
    outputLabel: steps[index + 1]?.name || 'DataUnfold',
    originalPrompt: pipeline.goal || '',
    revision: JSON.stringify(pipeline),
    editableIndex: step.native ? null : pipeline.steps.findIndex((entry) => entry.id === step.stepId),
    editableCount: pipeline.steps?.length || 0,
  }));
};

export const buildConnectorPreparationItem = (connector, step, caseid, reviewHelp) => ({
  ...connector, preparationCaseId: caseid, preparationStep: step, preparationMode: step.mode,
  preparationHelp: [step.help, step.mode === 'review' ? reviewHelp : ''].filter(Boolean).join('\n\n'),
});

export const buildConnectorPreparationNodes = (inputs = []) => inputs.flatMap((node) => (
  (node.preparationSteps || []).map((step, index) => ({
    key: `preparation:${node.key}:${step.id}`, id: `preparation:${node.key}:${step.id}`,
    kind: 'preparation', nodeType: 'preparation', label: step.name,
    logicalGroup: { id: `preparation:${node.key}`, title: node.preparationGroupTitle,
      original_prompt: step.originalPrompt || node.preparationGroupTitle },
    item: buildConnectorPreparationItem(node.item, step, node.caseid, node.preparationReviewHelp),
    sourceNodeKey: node.key, preparation: { ...step, index },
    isEnabled: node.isEnabled,
  }))
));

export const preparationNodeKey = (node, step) => `preparation:${node.key}:${step.id}`;
